import { SessionState } from "sip.js"
import { SessionType } from "./utils.js";
import { InvalidSessionStateException, InvalidSessionTypeException } from "./exceptions.js";


export class Session {
	#state
	#onHold
	#muted
	#sessionType;
	#sipSession;
	#userAgent;
	#remoteContact;

	constructor(session, type, userAgent) {
		// when getting a call / initiating a call, a new instance of Session is created.
		this.#sipSession = session;
		this.#sessionType = type;
		this.#userAgent = userAgent;

		const that = this;
		session.stateChange.addListener(async (newState) => {
			that.state = newState;
			switch (newState) {
				case SessionState.Initial:
					that.#setupLocalMedia();
					break;
				case SessionState.Established:
					that.#userAgent.getListeners.onCallRinging(that.#sessionType, { candidate: that.getRemoteContact})
					that.#setupRemoteMedia();
					break;
				case SessionState.Terminating:
				case SessionState.Terminated:
					const remoteContact = that.getRemoteContact;
					that.#userAgent.getListeners.onCallHangup(that.#sessionType, {candidate: remoteContact});
					that.remoteContact = undefined;
					that.#cleanupMedia();
					break;
				default:
					// InvalidSession transition
					break;
			}
		});
	}

	get getRemoteContact() {
		return this.#remoteContact;
	}

	/**@param {string} contact*/
	set remoteContact(contact) {
		this.#remoteContact = contact;
	}

	/**@param {SessionState} newSessionState*/
	set state(newSessionState) {
		this.#state = newSessionState;
	}

	async accept() {
		if(this.#sessionType === SessionType.Outgoing){
			throw new InvalidSessionTypeException('Session.accept', SessionType.Incoming)
		}
		if(this.#sipSession.state !== SessionState.Initial){
			throw new InvalidSessionStateException('Session.accept', 'Session state is not Initial')
		}
		try{
			await this.#sipSession.accept();
			return true;
		}
		catch(err){
			console.error('Error in Session.accept:', err);
			return false;
		}
	}

	async end() {
		if([SessionState.Terminating, SessionState.Terminated].includes(this.#state)){
			throw new InvalidSessionStateException('Session.end', 'Session state is already Terminated');
		}
		try{
			if(this.#state === SessionState.Established) await this.#sipSession.bye();
			else if(this.#sessionType === SessionType.Incoming) await this.#sipSession.reject(); 
			else if(this.#sessionType === SessionType.Outgoing) await this.#sipSession.cancel();
			return true;
		}
		catch(err){
			console.error('Error in Session.end:', err);
			return false;
		}
	}

	#cleanupMedia() {
		this.#userAgent.localAudioElement.srcObject = null
		this.#userAgent.remoteAudioElement.srcObject = null
	}

	#setupLocalMedia() {
		let senders = this.#sipSession?.sessionDescriptionHandler?.peerConnection?.getSenders();
		this.#setupMediaStream(senders, this.#userAgent.localAudioElement);
	}

	#setupRemoteMedia() {
		let receivers = this.#sipSession?.sessionDescriptionHandler?.peerConnection?.getReceivers();
		this.#setupMediaStream(receivers, this.#userAgent.remoteAudioElement, true);
	}

	#setupMediaStream(party, audioElement, playState=false) {
		let stream = new MediaStream();
		party.forEach(sender => {
			if (sender.track && sender.transport) stream.addTrack(sender.track);
		});
		audioElement.srcObject = stream;
		audioElement.onloadedmetadata = e => {
			if(playState) {
				audioElement.play()
				.then(() => {
					console.log("remote media playing")
				})
				.catch(error => console.error(error))
			}
		}
	}

	get getSession() {
		return this.#sipSession
	}

	get holdState() {
		return this.#onHold;
	}

	/**@param {boolean} isHold */
	set setHoldState(isHold) {
		this.#onHold = isHold;
	}

	get muteState() {
		return this.#muted;
	}

	/**@param {boolean} isMuted */
	set setHoldState(isMuted) {
		this.#muted = isMuted;
	}

	#changeMutedState(newMutedState) {
		const peerConnection = this.#sipSession.sessionDescriptionHandler.peerConnection;
		const sender = peerConnection.getSenders();
		sender.forEach(function (stream) {
			if (stream.track && stream.transport) {
				stream.track.enabled = !newMutedState
			}
		});
	}

	mute() {
        this.#changeMutedState(true)
	}

	unmute() {
        this.#changeMutedState(false)
	}

	#changeHoldState(newHoldState) {
		const sessionDescriptionHandlerOptions = this.#sipSession.sessionDescriptionHandlerOptionsReInvite;
		sessionDescriptionHandlerOptions.hold = newHoldState;

		const peerConnection = this.#sipSession.sessionDescriptionHandler.peerConnection;
		const sender = peerConnection.getSenders();

		// check current state, i.e.muted/unmuted and maintain the state in onAccept
		let trackState = null;
		sender.forEach(function (stream) {
			if (stream.track && stream.transport) {
			if (trackState === null) trackState = stream.track.enabled;
			}
		});

		
		this.#sipSession.sessionDescriptionHandlerOptionsReInvite = sessionDescriptionHandlerOptions;
		const inviteOptions = {
			requestDelegate: {
				onAccept: () => {
					const sessionDescriptionHandler = this.#sipSession.sessionDescriptionHandler;
					sessionDescriptionHandler?.enableSenderTracks(trackState);
					sessionDescriptionHandler?.enableReceiverTracks(newHoldState);
				},
				onReject: () => {
					const sessionDescriptionHandler = this.#sipSession.sessionDescriptionHandler;
					sessionDescriptionHandler?.enableReceiverTracks(false);
					sessionDescriptionHandler?.enableSenderTracks(false);
				},
			},
		};
		return this.#sipSession.invite(inviteOptions);
	}

	hold() {
        this.#changeHoldState(true);
	}

	unhold() {
        this.#changeHoldState(false);
	}

	async sendDTMF(signal) {
		if(this.#state !== SessionState.Established){
			throw new InvalidSessionStateException('Session.sendDTMF', 'Session state is not Established')
		}
		try{
			const options = {
				requestOptions: {
				body: {
					contentDisposition: "render",
					contentType: "application/dtmf-relay",
					content: `Signal=${signal}\r\nDuration=1000`,
				},
				},
			};
			await this.#sipSession.info(options);
			return true;
		}
		catch(err){
			console.error('Error in Session.sendDTMF:', err);
			return false;
		}
	}
}